import { ComponentType } from 'react';
import { IconProps } from '@velocity-exchange/icons';
import { ModalParams } from 'src/components/Modals/types';
import { MarketType } from '@velocity-exchange/sdk';

export type NavigationOptionKey =
	| 'grafanaDashboard'
	| 'leaderboard'
	| 'perpetuals'
	| 'spot'
	| 'swap'
	| 'driftStaking'
	| 'insuranceStaking'
	| 'driftEarn'
	| 'borrowLend'
	| 'portfolio'
	| 'settings'
	| 'docs'
	| 'referrals'
	| 'github'
	| 'twitter'
	| 'discord'
	| 'feedback'
	| 'terms'
	| 'disclaimers'
	| 'fundingHistory'
	| 'stats'
	| 'marketMakingVaults'
	| 'insuranceFundStakingHistory'
	| 'error'
	| 'dSolStaking'
	| 'vaults'
	| 'governance'
	| 'amplify'
	| 'superstakesol'
	| 'addressBook';

export type NavigationOptionKeyWithoutError = Exclude<
	NavigationOptionKey,
	'error'
>;

export type NavigationCategory =
	| 'portfolio'
	| 'trade'
	| 'earn'
	| 'vaults'
	| 'leaderboard'
	| 'more';

export enum NavigationCase {
	OPEN_WINDOW,
	NAVIGATE_TO_ROUTE,
	OPEN_MODAL,
	OPEN_WIDGET,
}

export type RouteNavigationResult = {
	type: NavigationCase.NAVIGATE_TO_ROUTE;
	route: string;
	opts?: {
		/**
		 * Used by `useNavigationState` to know that the navigation result should
		 * be re-routed to the previous favorite market.
		 */
		isMarketNavigation?: boolean;
		marketNavigationType?: MarketType;
		queryOption?: string;
	};
};

export type ModalNavigationResult = {
	type: NavigationCase.OPEN_MODAL;
	modal: ModalParams;
};

export type WindowNavigationResult = {
	type: NavigationCase.OPEN_WINDOW;
	href: string;
};

export type WidgetNavigationResult = {
	type: NavigationCase.OPEN_WIDGET;
	formId: number;
};

export type NavigationResult =
	| RouteNavigationResult
	| ModalNavigationResult
	| WindowNavigationResult
	| WidgetNavigationResult;

export interface NavigationOption {
	key: NavigationOptionKey;
	icon: ComponentType<IconProps>;
	handler: (opts?: RouteNavigationResult['opts']) => NavigationResult;
	hideCondition?: boolean;
}

export interface NavigationTagOptions {
	hotTag?: boolean;
	newTag?: boolean;
	/**
	 * Renders a neutral tag with the given string.
	 */
	tagLabel?: string;
}

export interface DesktopNavigationOptionItem
	extends NavigationOption, NavigationTagOptions {
	name: string;
	description?: string;
	popOutWindow?: boolean;
	isExternalLink?: boolean;
}

export interface DesktopCategoryOption extends NavigationTagOptions {
	type: 'categoryOption';
	categoryName: NavigationCategory;
	options: DesktopNavigationOptionItem[];
	hideCondition?: boolean;
	className?: string;
	hideOnMobileNav?: boolean;
	handler?: () => NavigationResult;
}

export interface DesktopStaticOption extends NavigationTagOptions {
	type: 'staticOption';
	categoryName: NavigationCategory;
	handler: () => NavigationResult;
	hideCondition?: boolean;
	className?: string;
	hideOnMobileNav?: boolean;
}

export type DesktopNavigationOption =
	| DesktopCategoryOption
	| DesktopStaticOption;

export type DesktopNavigationOptions = Record<
	NavigationCategory,
	DesktopNavigationOption
>;

export interface MobileNavigationOptionItem
	extends NavigationOption, NavigationTagOptions {
	name: string;
	isExternalLink?: boolean;
}

export interface MobileCategoryOption extends Omit<
	DesktopCategoryOption,
	'options'
> {
	options: MobileNavigationOptionItem[];
}

export type MobileStaticOption = DesktopStaticOption;

export type MobileNavigationOption = MobileCategoryOption | MobileStaticOption;

export type NavigationState = {
	handleNavigation: (navigationResult: NavigationResult) => void;
	navigateToRoute: (target: NavigationOptionKeyWithoutError) => void;
};
